import React from 'react';

import { toast } from 'react-toastify';

import { FILE_NUMBER_LIMIT } from 'components/Dropzone/Dropzone';
import Notification, { NotificationType } from 'components/Notification';
import { SuccessNotification } from 'enums/SuccessNotification';

import styles from './NotificationManager.module.scss';

const NOTIFICATION_CLOSE_DELAY = 3000;

export enum FailureNotificationType {
  DocumentIsTooBig = 'DocumentIsTooBig',
  DocumentNotUploaded = 'DocumentNotUploaded',
  ContractorEmailAlreadyExists = 'ContractorEmailAlreadyExists',
  BorrowerEmailAlreadyExists = 'BorrowerEmailAlreadyExists',
  CoBorrowerEmailAlreadyExists = 'CoBorrowerEmailAlreadyExists',
  UnknownError = 'UnknownError',
  CustomError = 'CustomError',
  FileLimitError = 'FileLimitError',
  EncryptedDocument = 'EncryptedDocument',
  IdDocumentValidationError = 'IdDocumentValidationError',
  InputsValidationErrors = 'InputsValidationErrors',
  RestrictedState = 'RestrictedState',
  RestrictedZipCode = 'RestrictedZipCode',
  TooManyRequest = 'TooManyRequest',
  ResumeProcessLinkExpired = 'ResumeProcessLinkExpired',
}

export const notifySuccess = (message: SuccessNotification) => {
  toast(<Notification message={message} type={NotificationType.Success} />, {
    position: 'bottom-center',
    autoClose: NOTIFICATION_CLOSE_DELAY,
    hideProgressBar: true,
    pauseOnHover: true,
    className: styles.notificationContainer,
    bodyClassName: styles.notificationBody,
  });
};

const getFailureNotificationMessage = (type: FailureNotificationType, customValue?: string) => {
  switch (type) {
    case FailureNotificationType.DocumentIsTooBig:
      return 'Document is larger than 10 Mb and cannot be uploaded';
    case FailureNotificationType.BorrowerEmailAlreadyExists:
      return 'This email is already used by another user. Please use different email';
    case FailureNotificationType.CoBorrowerEmailAlreadyExists:
      return "We're unable to use this email address to proceed with the application. Please contact the HFS team for assistance.";
    case FailureNotificationType.DocumentNotUploaded:
      return 'Document has not been uploaded';
    case FailureNotificationType.UnknownError:
      return 'Something went wrong, please refresh the page or try again later';
    case FailureNotificationType.FileLimitError:
      return `Too many files: please upload no more than ${FILE_NUMBER_LIMIT} documents at the same time`;
    case FailureNotificationType.EncryptedDocument:
      return `Error: The document you attempted to upload cannot be read as it is encrypted / password protected. Please re-upload.`;
    case FailureNotificationType.IdDocumentValidationError:
      return `Document upload failure.  Please re-upload and ensure the document is not blurry and is legible.`;
    case FailureNotificationType.ContractorEmailAlreadyExists:
      return (
        <div>
          Contractor with <span className="body_text--alternative_bold">{customValue}</span> email already exists.
          Please, enter another email.
        </div>
      );
    case FailureNotificationType.RestrictedState:
      return 'We do not currently offer loan funding in your state. Restricted states: South Dakota, Connecticut.';
    case FailureNotificationType.RestrictedZipCode:
      return 'Address provided is not in the U.S. 50 states or D.C.';
    case FailureNotificationType.InputsValidationErrors:
      return 'Error: Please scroll up to review one or more entries that you submitted in error, then try to resubmit.';
    case FailureNotificationType.TooManyRequest:
      return 'Please wait before requesting a new code';
    case FailureNotificationType.ResumeProcessLinkExpired:
      return 'This link is invalid or has expired.';
    default:
      return customValue || 'Error';
  }
};

export const notifyFailure = (type: FailureNotificationType, customInfo?: string) => {
  const message = getFailureNotificationMessage(type, customInfo);

  toast(<Notification message={message} type={NotificationType.Failure} />, {
    position: 'bottom-center',
    autoClose: NOTIFICATION_CLOSE_DELAY,
    hideProgressBar: true,
    pauseOnHover: true,
    className: styles.notificationContainerFailure,
    bodyClassName: styles.notificationBody,
  });
};

export const notifyProveLinkExpired = (handleCloseEvent: () => void) => {
  const message = (
    <>
      The authentication link has expired, please{' '}
      <span className={styles.link} onClick={handleCloseEvent}>
        click here
      </span>{' '}
      to proceed.
    </>
  );

  toast(<Notification message={message} type={NotificationType.Failure} isIcon={false} textProveFailure />, {
    position: 'bottom-center',
    autoClose: false,
    hideProgressBar: true,
    pauseOnHover: true,
    closeOnClick: false,
    className: styles.notificationContainerProveFailure,
    bodyClassName: styles.notificationBodyProveFailure,
    onClose: () => {},
  });
};

export const closeAllNotification = () => {
  toast.dismiss();
};
